import { Injectable, signal } from '@angular/core';

/**
 * HERO LOGO ANCHOR
 * ----------------
 * There is exactly **one** logo element in the app, and it lives in the nav bar.
 * On the home page it starts out flown down onto the hero at full size and
 * shrinks into the nav as the page scrolls; everywhere else it simply sits in
 * the nav. Because it is a single element it can never appear in two places at
 * once — that is a property of the structure, not of the timing.
 *
 * This service is the handshake between the two: the home hero registers an
 * empty, correctly-sized placeholder as the "anchor", and the nav bar flies its
 * logo to wherever that placeholder currently is. Neither component needs to
 * know the other exists, and no component reaches into another's DOM.
 */
@Injectable({ providedIn: 'root' })
export class HeroLogoService {
  /** The placeholder the logo should rest on at scroll position zero. */
  readonly anchor = signal<HTMLElement | null>(null);

  register(el: HTMLElement): void {
    this.anchor.set(el);
  }

  /**
   * Guarded by identity: during a route change the incoming page can register
   * its anchor before the outgoing one is destroyed, and an unguarded release
   * would then clear the new page's anchor.
   */
  release(el: HTMLElement): void {
    if (this.anchor() === el) this.anchor.set(null);
  }
}
